import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import { Button, Form, Input, Radio, DatePicker, Switch, type FormInstance } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import { DraggableTable } from 'components';
import { TMealPlanFormData, TMealPlanItem } from 'interfaces/mealPlan';
import { useMealPlanCategories } from 'hooks';

import emptyImgIcon from 'images/storeImage/emptyImage.svg';
import { getTableColumns } from './schema';
import { MealPlanFormContainer, ImageContainer, TableWrapper, SwitchWrapper } from './styles';

const { useForm } = Form;

const initialMealPlanItem: TMealPlanItem = {
  mealPlanItemIdx: null,
  sortBy: 0,
  mealPlanItemName: null,
  description: null,
  calories: null,
  natrium: null,
  carbohydrate: null,
  fat: null,
  protein: null,
  sugar: null,
  isActive: true
};
const initialData: TMealPlanFormData = {
  categoryId: null,
  serveDate: moment(),
  mealPlanName: null,
  content: null,
  isUseMealPlanItem: true,
  isUseSatisfactionSurvey: true,
  isDeleteImage: false,
  items: []
};

const convertToNumber = (value?: string | number): number => {
  return value ? Number(value) : null;
};
interface TMealPlanFormProps {
  children: React.ReactNode;
  form: FormInstance<TMealPlanFormData>;
  formData?: TMealPlanFormData;
  onSubmit?: (values: TMealPlanFormData) => void;
}

const MealPlanForm = ({ children, form, formData, onSubmit }: TMealPlanFormProps) => {
  const inputRef = useRef<HTMLInputElement>();
  const [currentForm] = useForm(form);

  const [dataSource, setDataSource] = React.useState<TMealPlanItem[]>([]);
  const [deletedDataSource, setDeletedDataSource] = React.useState<TMealPlanItem[]>([]);
  const [imageInfo, setImageInfo] = React.useState({ file: null, previewURL: '' });
  const [isDeleteImage, setIsDeleteImage] = React.useState<boolean>(false);

  const { data: categories } = useMealPlanCategories();

  useEffect(() => {
    if (formData) {
      const imageUrl = formData?.mealPlanImage;
      const previewURL = imageUrl instanceof File ? URL.createObjectURL(imageUrl) : imageUrl;
      const serveDate = formData?.serveDate ? moment(formData?.serveDate) : null;
      const items = formData?.items?.length ? formData?.items : [initialMealPlanItem];

      currentForm.setFieldsValue({ ...formData, serveDate } as object);

      setDataSource(items);
      setImageInfo({ file: null, previewURL });
    } else {
      setDataSource([initialMealPlanItem]);
    }
  }, [currentForm, formData]);

  const handleMealPlanAddItem = () => {
    const sortBy = Math.floor(Math.random() * 1000) + 10;
    setDataSource((prevState) => [...prevState, { ...initialMealPlanItem, sortBy }]);
  };
  const handleMealPlanDeleteItem = (sortBy: number) => {
    setDataSource((prevData) => {
      const newData = [...prevData];
      const index = newData.findIndex((item) => sortBy === item.sortBy);
      const deletedData = newData.splice(index, 1);

      if (deletedData[0]?.mealPlanItemIdx) {
        setDeletedDataSource((prevDeletedData) => prevDeletedData.concat(deletedData));
      }
      return newData;
    });
  };

  const imageUploadOnClick = () => inputRef?.current?.click();

  const handleImageChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.files.length) {
      const file = target.files[0];
      setImageInfo({ file, previewURL: URL.createObjectURL(file) });
      setIsDeleteImage(false);
    }
  };

  const handleRowChange = (values: TMealPlanItem[]) => setDataSource(values);

  const handleInputChange = (key, field, value) => {
    setDataSource((prevData) => {
      const newData = [...prevData];
      const index = newData.findIndex((item) => key === item.sortBy);
      if (index > -1) {
        newData[index] = { ...newData[index], [field]: value };
      }
      return newData;
    });
  };

  const handleRemoteImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setImageInfo({ file: null, previewURL: '' });
    setIsDeleteImage(true);
  };

  const handleSubmit = (values: TMealPlanFormData) => {
    const inActiveData = deletedDataSource.map((data) => ({ ...data, isActive: false }));
    const activeData = dataSource.map((data) => ({ ...data, isActive: true }));

    const items = activeData.concat(inActiveData).map((item, index) => ({
      ...item,
      sortBy: index + 1,
      calories: convertToNumber(item.calories),
      carbohydrate: convertToNumber(item.carbohydrate),
      protein: convertToNumber(item.protein),
      fat: convertToNumber(item.fat),
      natrium: convertToNumber(item.natrium),
      sugar: convertToNumber(item.sugar)
    }));
    onSubmit?.({ ...values, mealPlanImage: imageInfo.file, isDeleteImage: !imageInfo?.file && isDeleteImage, items });
  };

  const validateMealPlanItems = () => {
    const isUseMealPlanItem = currentForm.getFieldValue('isUseMealPlanItem');
    const hasError = isUseMealPlanItem && dataSource.filter(({ mealPlanItemName }) => !mealPlanItemName)?.length;
    if (hasError) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  const columns = getTableColumns({ onInputChange: handleInputChange, onRowDelete: handleMealPlanDeleteItem });
  return (
    <MealPlanFormContainer>
      <Form autoComplete="off" form={currentForm} initialValues={initialData} onFinish={handleSubmit}>
        <div className="title">식단표 설명</div>
        {/* serveDate */}
        <Form.Item name="serveDate" label="날짜" rules={[{ required: true, message: '날짜를 선택해주세요.' }]}>
          <DatePicker style={{ width: '200px', height: '40px', borderRadius: '5px' }} />
        </Form.Item>
        {/* categoryId */}
        <Form.Item name="categoryId" label="카테고리" rules={[{ required: true, message: '카테고리를 선택해주세요.' }]}>
          <Radio.Group>
            {categories?.map(({ categoryId, categoryName }) => (
              <Radio.Button key={categoryId} value={categoryId}>
                {categoryName}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
        {/* mealPlanName */}
        <Form.Item name="mealPlanName" label="식단명" rules={[{ required: true, message: '식단명을 입력해주세요.' }]}>
          <Input placeholder="예: [코스A] 김치찌개 정식" maxLength={30} />
        </Form.Item>
        {/* mealPlanImage */}
        <Form.Item name="mealPlanImage" label="이미지">
          <ImageContainer role="presentation" onClick={imageUploadOnClick}>
            {!imageInfo?.previewURL ? (
              <div className="container-center">
                <img src={emptyImgIcon} alt="img-preview" />
                <div style={{ fontSize: '12px' }}>클릭하여 이미지를</div>
                <div style={{ fontSize: '12px', lineHeight: '12px' }}>선택하세요</div>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <img alt="previewImage" className="preview-image" draggable={false} src={imageInfo?.previewURL} />
                <CloseCircleFilled onClick={handleRemoteImage} />
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              draggable={false}
              src={imageInfo?.previewURL}
              onChange={handleImageChange}
            />
          </ImageContainer>
        </Form.Item>
        {/* content */}
        <Form.Item name="content" label="원산지 구성">
          <Input placeholder="예: 돼지고기(국내산), 콩(중국산), 배추김치(배추(국산), 고춧가루(중국산)), 닭고기(프랑스산)" />
        </Form.Item>

        <div className="title">추가 설정</div>
        {/* isUseMealPlanItem */}
        <SwitchWrapper>
          <Form.Item name="isUseMealPlanItem" valuePropName="checked" label="식단구성 사용" rules={[{ required: true }]}>
            <Switch />
          </Form.Item>
          <span className="switch-label">※ 식단 구성을 사용자에게 노출 할 지 설정합니다.</span>
        </SwitchWrapper>

        <div className="title">
          <span>식단 구성</span>
          <span style={{ fontSize: '14px', color: '#999999', paddingLeft: '4px', fontWeight: 500 }}>(최대 10개)</span>
        </div>
        <Form.Item
          name="mealPlanItems"
          rules={[{ message: '식단 구성을 사용중인 경우 메뉴명을 전부 입력해주세요.', validator: validateMealPlanItems }]}
        >
          <TableWrapper>
            <DraggableTable
              rowKey="sortBy"
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              onChange={handleRowChange}
            />
          </TableWrapper>
        </Form.Item>
        <Button type="primary" className="item-add-button" disabled={dataSource?.length >= 10} onClick={handleMealPlanAddItem}>
          + 식단 추가
        </Button>
        {/* isUseSatisfactionSurvey */}
        <SwitchWrapper>
          <Form.Item name="isUseSatisfactionSurvey" valuePropName="checked" label="식단표 설문 사용" rules={[{ required: true }]}>
            <Switch />
          </Form.Item>
          <span className="switch-label">※ 사용자에게 식단표 설문를 진행 할 지 설정합니다.다.</span>
        </SwitchWrapper>
        <div>{children}</div>
      </Form>
    </MealPlanFormContainer>
  );
};

export default MealPlanForm;
